import { setPlanProperties } from '@/lib/analytics';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface SubscriptionData {
  planId: string | null;
  planKey: string | null;
  planName: string | null;
  subscriptionStatus: string | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  planPriceId: string | null;
  billingCycle: string | null;
  price: number | null;
  isOnTrial: boolean;
  /**
   * Stripe owns this subscription's lifecycle. Anything that offers a
   * PLATFORM-trial action (the day-7 paywall, "continue on Free", the countdown
   * banner) must exclude these: choose_free_after_trial refuses Stripe-backed
   * rows, so showing that escape hatch to one of them is a button that silently
   * does nothing while their card is charged.
   */
  isStripeBacked: boolean;
  /** Set by the cancel flow (or Stripe): access ends at currentPeriodEnd. */
  cancelAtPeriodEnd: boolean;
  /** Stripe pause_collection resume date, when the customer paused instead of cancelling. */
  pausedUntil: string | null;
  /** Trial clock has run out and no post-trial choice has been made yet. */
  isTrialExpired: boolean;
  daysUntilTrialEnd: number | null;
  hasActiveSubscription: boolean;
  isFreeTier: boolean;
  isPaidPlan: boolean;
}

/**
 * Hook to get the current organization's subscription information
 * @returns Subscription data and loading state
 */
export function useSubscription() {
  const { user, accessScope } = useAuth();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['subscription', user?.id, accessScope],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data: subscriptionData, error } = await supabase
        .rpc('get_organization_plan');

      if (error) throw error;

      const plan = subscriptionData?.[0];
      if (!plan) {
        return {
          planId: null,
          planKey: null,
          planName: null,
          subscriptionStatus: null,
          trialEndsAt: null,
          currentPeriodEnd: null,
          planPriceId: null,
          billingCycle: null,
          price: null,
          isOnTrial: false,
          isStripeBacked: false,
          cancelAtPeriodEnd: false,
          pausedUntil: null,
          isTrialExpired: false,
          daysUntilTrialEnd: null,
          hasActiveSubscription: false,
          isFreeTier: false,
          isPaidPlan: false,
        };
      }

      const isOnTrial = plan.subscription_status === 'trial';
      const isFreeTier = plan.plan_key === 'free';
      const hasActiveSubscription = !!plan.plan_id;
      const isPaidPlan = hasActiveSubscription && !isFreeTier;

      // Access decisions use the database clock (is_trial_expired, server_now),
      // not the customer's device clock. When the server verdict is missing
      // (a bundle that reaches users before its migration, or a rolled-back
      // migration) fall back to the previous device-clock check rather than
      // blocking every trial user behind an error screen.
      const serverVerdict = typeof plan.is_trial_expired === 'boolean' ? plan.is_trial_expired : null;
      const isTrialExpired = isOnTrial && !plan.is_stripe_backed && (
        serverVerdict ?? (!!plan.trial_ends_at && new Date(plan.trial_ends_at) <= new Date())
      );

      let daysUntilTrialEnd: number | null = null;

      if (isOnTrial && plan.trial_ends_at) {
        const trialEnd = new Date(plan.trial_ends_at);
        const now = plan.server_now ? new Date(plan.server_now) : new Date();
        const diffTime = trialEnd.getTime() - now.getTime();
        daysUntilTrialEnd = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      }

      setPlanProperties({ planKey: plan.plan_key, status: plan.subscription_status, billingCycle: plan.billing_cycle });
      return {
        planId: plan.plan_id,
        planKey: plan.plan_key,
        planName: plan.plan_name,
        subscriptionStatus: plan.subscription_status,
        trialEndsAt: plan.trial_ends_at,
        currentPeriodEnd: plan.current_period_end,
        planPriceId: plan.plan_price_id,
        billingCycle: plan.billing_cycle,
        price: plan.price ? Number(plan.price) : null,
        isOnTrial,
        isStripeBacked: !!plan.is_stripe_backed,
        cancelAtPeriodEnd: !!plan.cancel_at_period_end,
        pausedUntil: plan.paused_until ?? null,
        isTrialExpired,
        daysUntilTrialEnd,
        hasActiveSubscription,
        isFreeTier,
        isPaidPlan,
      };
    },
    enabled: !!user?.id && !!accessScope,
    // A "no plan" answer is never trusted from cache. A brand-new signup asks
    // before its organization (and platform trial) exists, and caching that
    // empty answer for two minutes sent people who had just signed up to the
    // paid plan picker, where checkout charges immediately (2026-10-02). A real
    // plan is still cached for two minutes.
    staleTime: (query) => (query.state.data?.planId && !query.state.data.isTrialExpired ? 2 * 60 * 1000 : 0),
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    subscription: data || {
      planId: null,
      planKey: null,
      planName: null,
      subscriptionStatus: null,
      trialEndsAt: null,
      currentPeriodEnd: null,
      planPriceId: null,
      billingCycle: null,
      price: null,
      isOnTrial: false,
      isStripeBacked: false,
      cancelAtPeriodEnd: false,
      pausedUntil: null,
      isTrialExpired: false,
      daysUntilTrialEnd: null,
      hasActiveSubscription: false,
      isFreeTier: false,
      isPaidPlan: false,
    },
    isLoading: isLoading || (!!user && !accessScope),
    refetch,
    /** True while any fetch is in flight, including a background refetch of a cached empty answer. */
    isFetching,
    error,
  };
}

/**
 * Hook to check if the current user is a super admin
 * @returns Super admin status and loading state
 */
export function useSuperAdmin() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['superAdmin', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;

      const { data, error } = await supabase
        .rpc('is_super_admin');

      if (error) {
        console.error('Error checking super admin status:', error);
        return false;
      }

      return data || false;
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
  });

  return {
    isSuperAdmin: data || false,
    isLoading,
  };
}

/**
 * Hook to get all available subscription plans (for super admins)
 * @returns All subscription plans data
 */
export function useSubscriptionPlans() {
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['subscriptionPlans'],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('plans')
        .select(`
          id,
          key,
          name,
          description,
          is_visible,
          stripe_product_id,
          trial_days,
          sort_order,
          plan_prices (
            id,
            billing_cycle,
            price,
            stripe_price_id,
            is_default,
            is_active
          ),
          plan_features (
            feature_key,
            is_enabled,
            limit_value,
            features (
              key,
              name,
              description,
              is_active,
              is_limit_type
            )
          )
        `)
        .eq('is_visible', true)
        .order('sort_order');

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    plans: data || [],
    isLoading,
    error,
  };
}

/**
 * Hook to get all available features (for super admins)
 * @returns All features
 */
export function useFeatureCatalog() {
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['featureCatalog'],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('features')
        .select(`
          id,
          key,
          name,
          description,
          is_active,
          is_limit_type,
          sort_order
        `)
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('name');

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
  });

  return {
    features: data || [],
    isLoading,
    error,
  };
}